// Shop images (logos, banners, service and team photos) are uploads in our
// Firebase Storage buckets, served at full size: a 1800 px banner shown 412 px
// wide, a 512 px logo shown at 36-64 px. server/routes/_img.get.ts resizes one
// to a set width and re-encodes it as WebP; these helpers build the URLs and
// the `srcset` that point at it. Anything else (another host, a data: URL, a
// local /public file) is left exactly as it was.

/** The widths the route will produce. A request for any other is snapped up to one of these. */
export const IMAGE_WIDTHS = [48, 64, 96, 128, 192, 256, 384, 512, 640, 828, 1080, 1280, 1600] as const

/** Our buckets only: the route is not a proxy for the rest of the internet. */
export const IMAGE_BUCKETS = ['workspace-369-us', 'workspace369.appspot.com', 'workspace-369.appspot.com', 'workspace-369.firebasestorage.app'] as const

/** The one cropped size the route makes: a share image (og:image), 1200×630. */
export const OG_IMAGE_WIDTH = 1200
export const OG_IMAGE_HEIGHT = 630

/** The narrowest candidate a fluid image offers. */
const FLUID_MIN_WIDTH = 384

/** The route's path on this site. */
export const IMAGE_ROUTE = '/_img'

/**
 * `url` in the one spelling the route accepts, or null when it is not a
 * Firebase Storage object in one of {@link IMAGE_BUCKETS}. Firebase download
 * URLs keep only `alt` and `token`; storage.googleapis.com URLs keep no query.
 * One spelling per image means one CDN entry per image and width: any other
 * spelling of the same file is refused, not resized again.
 */
export function canonicalImageUrl(url: unknown): string | null {
  if (typeof url !== 'string' || url.length > 2048) return null
  let parsed: URL
  try {
    parsed = new URL(url)
  }
  catch {
    return null
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.port) return null
  const buckets: readonly string[] = IMAGE_BUCKETS
  let path: string
  try {
    path = decodeURIComponent(parsed.pathname)
  }
  catch {
    return null
  }
  // Vectors stay sharp at any size and GIFs may move: both are left as they are.
  if (/\.(?:svg|gif)$/i.test(path)) return null
  if (parsed.hostname === 'firebasestorage.googleapis.com') {
    const match = parsed.pathname.match(/^\/v0\/b\/([^/]+)\/o\/[^/]+$/)
    if (!match || !buckets.includes(decodeURIComponent(match[1]!))) return null
    const query = new URLSearchParams()
    const alt = parsed.searchParams.get('alt')
    const token = parsed.searchParams.get('token')
    if (alt) query.set('alt', alt)
    if (token) query.set('token', token)
    const search = query.toString()
    return `https://firebasestorage.googleapis.com${parsed.pathname}${search ? `?${search}` : ''}`
  }
  if (parsed.hostname === 'storage.googleapis.com') {
    const bucket = parsed.pathname.split('/')[1] ?? ''
    if (!buckets.includes(bucket) || parsed.pathname.split('/').length <= 2) return null
    return `https://storage.googleapis.com${parsed.pathname}`
  }
  return null
}

/** Whether `url` is a Firebase Storage object in one of {@link IMAGE_BUCKETS}. */
export function isResizableImage(url: unknown): url is string {
  return canonicalImageUrl(url) !== null
}

/** The smallest produced width at or above `width` (the largest for anything past it). */
export function snapImageWidth(width: number): number {
  const wanted = Number.isFinite(width) && width > 0 ? width : 0
  return IMAGE_WIDTHS.find(w => w >= wanted) ?? IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1]!
}

/** The route URL for `src` at `width`, or `src` itself when it is not one of ours. */
export function resizedImageUrl(src: string, width: number): string {
  const canonical = canonicalImageUrl(src)
  if (!canonical) return src
  return `${IMAGE_ROUTE}?u=${encodeURIComponent(canonical)}&w=${snapImageWidth(width)}`
}

/**
 * For an `<img>` that failed: when it was loading a resized copy, drops
 * `srcset` and `sizes` so the browser loads `src` (the original) instead, and
 * returns true. False when the failure was not a resized copy.
 * plugins/resizedImageFallback.client.ts calls it for every image.
 */
export function fallBackToOriginalImage(img: unknown): boolean {
  if (typeof HTMLImageElement === 'undefined' || !(img instanceof HTMLImageElement)) return false
  if (!img.getAttribute('srcset')?.includes(`${IMAGE_ROUTE}?`)) return false
  img.removeAttribute('srcset')
  img.removeAttribute('sizes')
  return true
}

export interface ResponsiveImageAttrs {
  src: string
  srcset?: string
  sizes?: string
}

/**
 * The attributes for an `<img>` drawn `displayWidth` CSS pixels wide (its
 * widest, for a fluid one, with `sizes` saying how it scales). Our images get
 * a `srcset` (1x, 2x and 3x of that width; for a fluid one, every width up to
 * 2x) and the browser picks; `src` stays
 * the original, so a browser without `srcset` (or a failed resize) still shows
 * it. Not ours: `{ src }` only, so the markup is what it always was.
 */
export function responsiveImageAttrs(src: string | null | undefined, displayWidth: number, sizes?: string): ResponsiveImageAttrs {
  const url = src ?? ''
  if (!isResizableImage(url)) return { src: url }
  // A fixed-size image needs its 1x, 2x and 3x; a fluid one (with `sizes`)
  // every width from a phone's up to 2x its widest.
  const widths = sizes
    ? IMAGE_WIDTHS.filter(w => w >= FLUID_MIN_WIDTH && w <= snapImageWidth(displayWidth * 2))
    : [...new Set([1, 2, 3].map(density => snapImageWidth(displayWidth * density)))]
  return {
    src: url,
    srcset: widths.map(w => `${resizedImageUrl(url, w)} ${w}w`).join(', '),
    sizes: sizes ?? `${Math.round(displayWidth)}px`,
  }
}
